import type {BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {StickerItemSource} from '@Libraries/pmw-stock-media-library.types';
import type {StickerItemSourceType} from '@Libraries/pmw-stock-media-library.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {openPosterEditorItemEffectsModal} from '@Modals/poster-editor-item-effects-modal';
import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {ItemEffectsObject} from '@PosterWhiteboard/classes/item-effects.class';
import {EdgeType, ItemEffects} from '@PosterWhiteboard/classes/item-effects.class';
import {SyncToPageClock} from '@PosterWhiteboard/classes/sync-to-page-clock.class';
import type {AnimatedSprite} from '@Libraries/animated-sprite.library';
import {getFramesForSprite} from '@Libraries/animated-sprite.library';
import {Sprite} from '@PosterWhiteboard/items/fabric-sprite';
import type {DeepPartial} from '@/global';

export interface StickerItemObject extends BaseItemObject {
  hashedFilename: string;
  duration: number;
  frameRate: number;
  effects: ItemEffectsObject;
  highResAnimatedSprite: AnimatedSprite;
  screenAnimatedSprite: AnimatedSprite;
  stickerSource: StickerItemSourceType;
}

/**
 * The version at which we enabled uniform stroke for motion items.
 */
const VERSION_2 = 2;

/**
 * Default min duration for which stickers should be played
 */
const STICKER_MIN_PLAY_DURATION = 10;

export class StickerItem extends Item {
  declare _fabricObject: Sprite;
  public gitype: ITEM_TYPE.STICKER = ITEM_TYPE.STICKER;
  public frameRate = 0;
  public hashedFilename = '';
  public duration = 0;
  public effects: ItemEffects;
  public override version = 2;
  public htmlImageElement!: HTMLImageElement;
  public syncToPageClock: SyncToPageClock;
  public highResAnimatedSprite!: AnimatedSprite;
  public screenAnimatedSprite!: AnimatedSprite;
  public stickerSource!: StickerItemSourceType;
  /**
   * Sync to poster click function is cached in this variable so that it can be unmounted on item remove. Inline use of that
   * function doesn't work because of bind (https://stackoverflow.com/questions/28800850/jquery-off-is-not-unbinding-events-when-using-bind)
   */
  private readonly syncToPageClockFunction;

  public constructor(page: Page) {
    super(page);
    this.effects = new ItemEffects(this);
    this.syncToPageClock = new SyncToPageClock({
      getDuration: this.getDuration.bind(this),
      getCurrentTime: this.getCurrentTime.bind(this),
      getPageDuration: (): number => this.page.getDuration(),
      seek: this.seek.bind(this),
      pauseAtEnd: false,
      stopFunction: this.stopItemStream.bind(this),
    });
    this.syncToPageClockFunction = this.syncToPageClock.syncToPage.bind(this.syncToPageClock);
  }

  override get fabricObject(): Sprite {
    return this._fabricObject;
  }

  public override getPlayDuration(): number {
    return Math.max(this.getDuration(), STICKER_MIN_PLAY_DURATION);
  }

  public async getFabricObjectForItem(opts?: InitItemOpts): Promise<Sprite> {
    const currentSprite = this.getCurrentSprite();

    const frameImages = await getFramesForSprite(currentSprite);
    if (!frameImages[0]) {
      throw new Error(`No frame images for sprite: ${JSON.stringify(currentSprite)}`);
    }
    [this.htmlImageElement] = frameImages;

    return new Sprite(this.htmlImageElement, {
      ...super.getCommonOptions(),
      frameImages,
      frameHeight: this.htmlImageElement.height,
      frameWidth: currentSprite.frameWidth,
      frameTime: 1000 / currentSprite.frameRate,
      ...this.getScalesAndShadowToMaintainScaledDimensions(this.htmlImageElement.width, this.htmlImageElement.height, opts),
    });
  }

  public override getCopyableStyles(): CopyableItemStyles {
    const styles: CopyableItemStyles = {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      textBorder: this.border.toObject(),
      effects: this.effects.toObject(),
    };

    if (this.effects.edgeType === EdgeType.CURVED) {
      styles.rx = (this.effects.edgeThickness / 28) * 100;
      styles.ry = (this.effects.edgeThickness / 28) * 100;
    }

    return styles;
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<StickerItemObject> = {};
    if (copiedStyles.effects !== undefined) {
      stylesObj.effects = copiedStyles.effects;
    }

    if (copiedStyles.rx !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        edgeType: EdgeType.CURVED,
        edgeThickness: (copiedStyles.rx / 100) * 28,
      };
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        tint: this.effects.tint,
        tintColor: this.effects.tintColor,
      };
    }

    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.updateFromObject({...commonStylesObj, ...stylesObj});
  }

  public override getDuration(): number {
    return this.duration;
  }

  public getCurrentTime(): number {
    return this.fabricObject.getCurrentTime();
  }

  public override seek(time: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const seekTime = time % this.getDuration();
      this.fabricObject
        .seek(seekTime)
        .then(() => {
          this.page.fabricCanvas.requestRenderAll();
          resolve();
        })
        .catch(reject);
    });
  }

  public override async stop(): Promise<void> {
    void super.stop();
    await this.fabricObject.stop();
  }

  public override async play(): Promise<void> {
    await this.fabricObject.play();
  }

  public override async pause(): Promise<void> {
    void super.pause();
    await this.fabricObject.pause();
  }

  public async stopItemStream(): Promise<void> {
    if (!this.fabricObject.isPaused()) {
      await this.stop();
    }
  }

  public override isStreamingMediaItem(): boolean {
    return true;
  }

  public override copyVals(obj: DeepPartial<StickerItemObject>): void {
    const {effects, ...itemObj} = obj;
    super.copyVals(itemObj);
    this.effects.copyVals(effects);
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.effects.applyItemEffects();
    await this.effects.applyBorderEffects();
  }

  protected override async fixChanges(applyVersionFixesData: Record<string, any>): Promise<void> {
    await super.fixChanges(applyVersionFixesData);
    this.applyFixForVersion2();
  }

  private applyFixForVersion2(): void {
    if (!this.hasUniformStroke()) {
      this.border.solidBorderThickness = Math.round(this.border.solidBorderThickness * this.fabricObject.scaleX);
      this.version = VERSION_2;
    }
  }

  public override onRemove(): void {
    super.onRemove();
    this.fabricObject.onRemove();
    this.page.off('time:updated', this.syncToPageClockFunction);
  }

  public override async onItemAddedToPage(): Promise<void> {
    this.page.on('time:updated', this.syncToPageClockFunction);

    if (this.page.isPlaying) {
      await this.play();
    }
  }

  public hasUniformStroke(): boolean {
    return this.version >= VERSION_2;
  }

  public isAiAnimatedSticker(): boolean {
    return this.stickerSource === StickerItemSource.PMW_AI_ANIMATED_STICKER;
  }

  public isAnimatedSticker(): boolean {
    return this.stickerSource === StickerItemSource.PMW_ANIMATED_STICKER;
  }

  private getCurrentSprite(): AnimatedSprite {
    if (this.page.poster.isHighRes) {
      return this.highResAnimatedSprite;
    }

    return this.screenAnimatedSprite;
  }

  public override toObject(): StickerItemObject {
    return {
      ...super.toObject(),
      effects: this.effects.toObject(),
      hashedFilename: this.hashedFilename,
      duration: this.duration,
      frameRate: this.frameRate,
      highResAnimatedSprite: this.highResAnimatedSprite,
      screenAnimatedSprite: this.screenAnimatedSprite,
      stickerSource: this.stickerSource,
    };
  }

  protected override onItemDoubleTapped(): void {
    openPosterEditorItemEffectsModal();
  }
}
